<template>
	<v-row no-gutters class="pa-2 overflow-auto" style="height: 100%">
		<!-- Time tracking of the user in this page -->
		<UserTimeTracker page-key="ccmCarePlan" />

		<v-col cols="12" class="pa-1" style="height: 100%">
			<v-card style="height: 100%; min-height: 300px" :loading="dataLoading">
				<v-card-title class="py-2 py-lg-4">
					<v-row no-gutters>
						<v-col class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
							{{ $t('ccm-care-plans-header') }}
						</v-col>
						<v-spacer />
						<v-col class="text-right">
							<ActionButton
								icon="mdi-file-delimited"
								tooltip="download-csv"
								:maxSize="25"
								:minSize="20"
								@clicked="downloadCSV()"
								:busy="downloading" />

							<v-icon
								color="primary"
								:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
								@click="synchronizeAddDrawers('ccmCarePlanAdd')">
								mdi-plus-circle
							</v-icon>
						</v-col>
					</v-row>
				</v-card-title>
				<v-card-text
					class="overflow-auto"
					:style="$vuetify.breakpoint.lgAndUp ? 'height: calc(100% - 64px)' : 'height: calc(100% - 48px)'">
					<v-data-table
						:items="carePlans"
						:headers="headers"
						:items-per-page="-1"
						style="overflow-y: auto"
						:height="pageHeight($vuetify.breakpoint.mdAndDown ? 93 : 77)"
						fixed-header
						dense
						sort-by="timestamp"
						sort-desc
						class="data-table-outline"
						:footer-props="{ 'items-per-page-text': '' }">
						<template v-slot:item="{ item, index }">
							<tr :class="getRowsForDataTable(index)" @click.stop="openViewDialog(item)">
								<td class="text-left px-1">
									{{ item.carePlanStatus }}
								</td>
								<td class="text-left px-1">
									{{ item.type }}
								</td>
								<td class="text-left px-1 truncate-text" style="max-width: 20vw">
									{{ item.description }}
								</td>
								<td class="text-left px-1">
									{{ item.performedBy }}
								</td>
								<td class="text-left px-1">
									{{ formatTimestamp(item.date) }}
								</td>

								<td class="text-left px-1">
									{{ formatDuration(item.timeSpentSeconds) }}
								</td>
								<td class="px-1 text-right">
									<ActionButton
										v-if="item.ccmRecordId === selectedCCMRecord.id"
										icon="mdi-delete"
										tooltip="delete"
										:maxSize="25"
										:minSize="20"
										@clicked="
											deleteId = item.id;
											showDeleteDialog = true;
										"
										:busy="deletingIds.includes(item.id)" />

									<v-tooltip left v-if="item.ccmRecordId === selectedCCMRecord.id">
										<template v-slot:activator="{ on }">
											<v-icon
												class="mt-1"
												color="primary"
												:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
												v-on="on"
												@click.stop="openEditDrawer(item)">
												mdi-pencil
											</v-icon>
										</template>
										{{ $t('edit') }}
									</v-tooltip>
								</td>
							</tr>
						</template>
						<template v-slot:[`footer.prepend`]>
							<v-row dense class="ma-0">
								<v-col class="text-left">
									<p class="ma-0">
										<v-tooltip top>
											<template v-slot:activator="{ on }">
												<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
											</template>
											{{ $t('need-to-select-the-correct-record') }}
										</v-tooltip>
									</p>
								</v-col>
							</v-row>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-col>
		<CCMCarePlanViewDialog
			:showDialog.sync="showViewDialog"
			v-if="showViewDialog && selectedCarePlan"
			:selectedCarePlan="selectedCarePlan"
			@closeDialog="showViewDialog = false" />

		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deletingIds.push(deleteId);
				deleteCarePlanEvent(deleteId);
				deleteId = -1;
			" />
	</v-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	getRowsForDataTable,
	formatTimestamp,
	formatDuration,
	shortenText,
	downloadCSVWithKeys,
	getFullName,
} from '@/utils/utilities';
import CCMCarePlanViewDialog from '../../../dialogs/CCMCarePlanViewDialog.vue';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';
import UserTimeTracker from '@/views/Client/components/UserTimeTracker.vue';
import ActionButton from '../ActionButton.vue';

export default {
	name: 'CCMCarePlan',
	components: { CCMCarePlanViewDialog, DeleteDialog, UserTimeTracker, ActionButton },

	data () {
		return {
			dataLoading: true,
			deleteId: -1,
			deletingIds: [],
			selectedCarePlan: null,
			showDeleteDialog: false,
			showViewDialog: false,
			downloading: false,
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			carePlans: (state) => state.ccmCarePlans.ccmCarePlansByPatientId,
			selectedCCMRecord: (state) => state.ccmRecords.ccmRecord,
		}),

		...mapGetters({
			pageHeight: 'pageHeight',
		}),

		headers () {
			return [
				{
					text: this.$t('status'),
					value: 'carePlanStatus',
					class: 'color_header px-1',
				},
				{
					text: this.$t('type'),
					value: 'type',
					class: 'color_header px-1',
				},
				{
					text: this.$t('description'),
					value: 'description',
					class: 'color_header px-1',
				},
				{
					text: this.$t('created-by'),
					value: 'performedBy',
					class: 'color_header px-1',
				},
				{
					text: this.$t('date'),
					value: 'date',
					class: 'color_header px-1',
				},
				{
					text: this.$t('time-spent-header'),
					sortable: false,
					value: 'timeSpentSeconds',
					class: 'color_header px-1',
				},
				{
					text: this.$t('actions'),
					sortable: false,
					value: '',
					align: 'right',
					class: 'pl-1 pr-3',
				},
			];
		},
	},

	methods: {
		...mapActions({
			getCarePlans: 'ccmCarePlans/getCCMCarePlansByPatientId',
			getSelectedCarePlan: 'ccmCarePlans/getCCMCarePlan',
			deleteCarePlan: 'ccmCarePlans/deleteCCMCarePlan',
			storeCCMRecordsByPatientId: 'ccmRecords/storeMinimalCCMRecordsByPatientId',
		}),

		getRowsForDataTable,
		formatTimestamp,
		shortenText,
		downloadCSVWithKeys,
		getFullName,
		formatDuration,

		async deleteCarePlanEvent (id) {
			await this.deleteCarePlan(id);
			await this.storeCCMRecordsByPatientId(this.detailedUser.userData.id);
			this.deletingIds = this.deletingIds.filter((item) => item !== id);
		},
		async openEditDrawer (carePlan) {
			if (carePlan.ccmRecordId !== 0) {
				await this.getSelectedCarePlan(carePlan.id);
				this.synchronizeEditDrawers('ccmCarePlanEdit');
			}
		},
		openViewDialog (carePlan) {
			this.selectedCarePlan = carePlan;
			this.showViewDialog = true;
		},
		downloadCSV () {
			this.downloading = true;
			downloadCSVWithKeys(this.carePlans, 'Care_Plans_for_' + getFullName(this.detailedUser.userData), {
				ids: ['ccmRecordId'],
				data: ['carePlanStatus', 'type', 'description', 'performedBy', 'performedByRole'],
				timestamps: ['date'],
				timeSpent: ['timeSpentSeconds'],
			});
			this.downloading = false;
		},
		synchronizeAddDrawers (drawer) {
			const parameters = {
				detailsDrawer: true,
				page: drawer,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		synchronizeEditDrawers (drawer) {
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'ccmDetails',
			});
		},
	},

	async mounted () {
		if (this.carePlans.length) {
			this.dataLoading = false;
		}
		await this.getCarePlans(this.detailedUser.userData.id);
		this.dataLoading = false;
	},
};
</script>
